import { ErrorHandler, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgIdleModule } from '@ng-idle/core';
import { NgProgressModule } from 'ngx-progressbar';
import { NgProgressHttpModule } from 'ngx-progressbar/http';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { AlertModule } from 'ngx-bootstrap/alert';
import { ButtonsModule } from 'ngx-bootstrap/buttons';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { SplitButtonModule } from 'primeng/splitbutton';
import { ApiResponseInternalServerErrorComponent } from './apiresponse/api-response-internal-server-error.component';
import { ApiResponseNotFoundComponent } from './apiresponse/api-response-not-found.component';
import { ApiResponseServiceUnavailableComponent } from './apiresponse/api-response-service-unavailable.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthCoreModule } from './auth/auth-core.module';
import { AuthModule } from './auth/auth.module';
import { CartCoreModule } from './cart/cart-core.module';
import { CustomerLookupService } from './customer/customer-lookups.service';
import { CustomerSearchService } from './customer/shared/customer-search.service';
import { UspvsHandlerModule } from './customer/uspvs/uspvs-handler.module';
import { MedicalExamCertificateMegaMenuModalComponent } from './driver/credential/issuance/requirement/medical-exam-certificate-megamenu-modal.component';
import { DriverModalsModule } from './driver/driver-modals.module';
import { MyExceptionHandler } from './exception';
import { FinanceCoreModule } from './finance/finance-core.module';
import { NotificationCoreModule } from './notification/notification-core.module';
import { NotificationModule } from './notification/notification.module';
import { CustomerPurgeWindow } from './purge/customer-purge.modal.component';
import { CustomerPurgeService } from './purge/customer-purge.service';
import { appInjector } from './shared/app-injector';
import { MegaMenuNavModule } from './shared/components/navigation/mega-menu/mega-menu.module';
import { RightNavBarModule } from './shared/components/navigation/right-nav-bar/right-nav-bar.module';
import { SearchResultService } from './shared/components/search/search-results/search-result.service';
import { SearchModule } from './shared/components/search/search.module';
import { SharedComponentsModule } from './shared/components/shared-components.module';
import { CoreModule } from './shared/core.module';
import { ClickedOutsideDirective } from './shared/directives/clickedOutsideElement/clickedOutsideElement.directive';
import { MaterialModule } from './shared/material.module';
import { SharedModule } from './shared/shared.module';
import { AppStoreModule } from './store/app-store.module';
import { TitleRegistrationService } from './titleregistration/services/titleregistration.service';
import { CommercialPermitService } from './commercialpermitting/services/commercial-permit.service';
import { DifferentAgentCartWindow } from './transaction/queue/modals/different-agent-cart-modal.component';
import { TransactionActiveWindow } from './transaction/queue/modals/transaction-active-modal.component';
import { BusinessTransactionRealtimeMessagingService } from './transaction/services/business-transaction-realtime-messaging.service';
import { TransactionCoreModule } from './transaction/transaction-core.module';
import { VehicleAddModalModule } from './vehicle/components/shared/vehicle/vehicle-add-modal/vehicle-add-modal.module';
import { VehicleSearchModule } from './vehicle/search/vehicle-search/vehicle-search.module';
import { VehicleLookupService } from './vehicle/shared/vehicle-lookups.service';
import { DeleteCustomerIdentificationWindow } from './purge/delete-customer-identification.modal.component';
import { IRPLookupService } from './irp/services/irp-lookup.service';
import { MotorCarrierService } from './irp/services/motor-carrier.service';
import { AircraftSearchModule } from './aircraft/components/aircraft-search/aircraft-search.module';
import { MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalRedirectComponent, MsalModule, MsalGuard, MsalBroadcastService } from '@azure/msal-angular';
import { Configuration as MSALConfiguration, InteractionType, LogLevel, PublicClientApplication, } from '@azure/msal-browser';
import { getAppSettings } from './shared/settings/app-settings.model';

@NgModule({
    declarations: [
        AppComponent,
        ApiResponseNotFoundComponent,
        ApiResponseInternalServerErrorComponent,
        ApiResponseServiceUnavailableComponent,
        ClickedOutsideDirective,
        CustomerPurgeWindow,
        DeleteCustomerIdentificationWindow
    ],
    providers: [
        BusinessTransactionRealtimeMessagingService,
        CustomerLookupService,
        CustomerPurgeService,
        CustomerSearchService,
        SearchResultService,
        TitleRegistrationService, // make it static
        CommercialPermitService,
        VehicleLookupService,
        IRPLookupService,
        MotorCarrierService,
        {
            provide: MSAL_INSTANCE,
            useFactory: () => {
                // get all appsettings from AppSettings config
                let appSettings = getAppSettings();
                // get all of the security settings from AppSettings in the security section
                let securitySettings = appSettings.security;
                const msalConfig: MSALConfiguration = {
                    auth: {
                        clientId: securitySettings.clientId,
                        authority: securitySettings.authority,
                        redirectUri: securitySettings.loginRedirectUri,
                        postLogoutRedirectUri: securitySettings.postLogoutRedirectUri,
                        navigateToLoginRequestUrl: false
                    },
                    cache: {
                        cacheLocation: securitySettings.cacheLocation,
                    },
                    system: {
                        loggerOptions: {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            loggerCallback: (logLevel, message, containsPii): void => {
                                console.log(message);
                            },
                            logLevel: LogLevel.Error,
                            piiLoggingEnabled: false
                        }
                    },
                };
                return new PublicClientApplication(msalConfig);
            },
            deps: [
            ]
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: (): MsalInterceptorConfiguration => {
                // get all appsettings from AppSettings config
                const appSettings = getAppSettings();
                const webApiUrl = appSettings.api.serviceUrl.replace(/\/+$/g, '') + '/';
                const protectedResourceMap = new Map<string, Array<string>>();
                protectedResourceMap.set(webApiUrl, [appSettings.security.scope]);
                // protectedResourceMap.set(appSettings.api.graphApiUrl, ['user.read']);
                return {
                    interactionType: InteractionType.Redirect,
                    protectedResourceMap,
                };
            },
            deps: []
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useValue: {
                interactionType: InteractionType.Redirect
            } as MsalGuardConfiguration
        },
        { provide: ErrorHandler, useClass: MyExceptionHandler }
    ],
    imports: [
        AccordionModule.forRoot(),
        AlertModule.forRoot(),
        AppRoutingModule,
        AppStoreModule,
        AuthCoreModule.forRoot(),
        AuthModule,
        BrowserModule,
        BrowserAnimationsModule,
        ButtonsModule.forRoot(),
        CartCoreModule.forRoot(),
        CoreModule.forRoot(),
        DriverModalsModule,
        FinanceCoreModule.forRoot(),
        MaterialModule.forRoot(),
        MegaMenuNavModule,
        NgIdleModule.forRoot(),
        NgProgressModule.withConfig({
            thick: true,
            spinner: false
        }),
        NgProgressHttpModule,
        NotificationModule,
        NotificationCoreModule.forRoot(),
        PaginationModule.forRoot(),
        RightNavBarModule,
        SearchModule,
        SharedModule.forRoot(),
        SharedComponentsModule,
        SplitButtonModule,
        TransactionCoreModule,
        UspvsHandlerModule,
        VehicleAddModalModule,
        VehicleSearchModule,
        AircraftSearchModule,
        HttpClientModule,
        MsalModule
    ],
    bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
    constructor(private injector: Injector) {
        appInjector(injector);
    }
}
